import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../../hooks';
import proofkitFilemakerService from '../../../../../services/proofkitFilemakerService';
import { ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Textarea } from '../../../../../components/ui';

const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function TemplatesFormEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateRecordId, setTemplateRecordId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    asunto: "",
    contenido_html: "",
    contenido_texto: "",
    variables: "",
  });

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setTemplateRecordId(null);

      let record = null;

      try {
        const res = await proofkitFilemakerService.getRecord(TEMPLATES_LAYOUT, id);
        record = res?.data || null;
      } catch (_err) {
        const findRes = await proofkitFilemakerService.findRecords(TEMPLATES_LAYOUT, {
          query: [{ ClavePrincipal: String(id) }],
          limit: 1,
          offset: 1,
        });
        record = Array.isArray(findRes?.data) ? findRes.data[0] : null;
      }

      if (!record) {
        throw new Error('Plantilla no encontrada');
      }

      const fieldData = record?.fieldData || {};
      setTemplateRecordId(record?.recordId || null);

      setFormData({
        nombre: fieldData.nombre || "",
        asunto: fieldData.subject || fieldData.asunto || "",
        contenido_html: fieldData.html || fieldData.contenido_html || "",
        contenido_texto: fieldData.contenido_texto || "",
        variables: fieldData.variables || "",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar la plantilla",
        variant: "destructive",
      });
      navigate("/campanas/templates");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await proofkitFilemakerService.updateRecord(TEMPLATES_LAYOUT, templateRecordId || id, {
        nombre: formData.nombre,
        subject: formData.asunto,
        html: formData.contenido_html,
      });
      toast({
        title: "Éxito",
        description: "Plantilla actualizada correctamente",
      });
      navigate("/campanas/templates");
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la plantilla",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando plantilla...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate("/campanas/templates")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Editar Plantilla</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información de la Plantilla</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asunto">Asunto del Email</Label>
                <Input
                  id="asunto"
                  name="asunto"
                  value={formData.asunto}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contenido_html">Contenido HTML</Label>
                <Textarea
                  id="contenido_html"
                  name="contenido_html"
                  value={formData.contenido_html}
                  onChange={handleChange}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contenido_texto">Contenido Texto Plano</Label>
                <Textarea
                  id="contenido_texto"
                  name="contenido_texto"
                  value={formData.contenido_texto}
                  onChange={handleChange}
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="variables">Variables (JSON)</Label>
                <Textarea
                  id="variables"
                  name="variables"
                  value={formData.variables}
                  onChange={handleChange}
                  rows={4}
                  className="font-mono text-sm"
                  placeholder='{"nombre": "John", "empresa": "Yool"}'
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/campanas/templates")}>
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
